import {useIssueCreateMetadata} from '@github-ui/issue-create/IssueCreateDataContext'
import type {RepositoryPickerRepository} from '@github-ui/item-picker/RepositoryPicker'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import type {DraftIssue} from '../content-preview-types'
import {useOnChangeCallback} from './use-on-change-callback'

function isEqual(left: string | undefined, right: string | undefined) {
  return left?.toLowerCase() === right?.toLowerCase()
}

export function useDraftIssueRepository(
  draftIssue: DraftIssue,
  resolvedDraftIssueRepo: RepositoryPickerRepository | null | undefined,
  loadingDraftIssueRepo: boolean,
  topRepos: RepositoryPickerRepository[],
) {
  const {repository: formRepository, setRepository: setFormRepository} = useIssueCreateMetadata()
  const isResolvingFormRepository = useRef(true)
  const lastKnownDraft = useRef<`${DraftIssue['id']}` | null>(null)
  const lastKnownFormRepository = useRef<RepositoryPickerRepository | null | undefined>(undefined)
  const onChange = useOnChangeCallback(draftIssue)

  // Reset on switching a draft
  useEffect(() => {
    if (lastKnownDraft.current !== draftIssue.id) {
      isResolvingFormRepository.current = true
      lastKnownFormRepository.current = undefined
      lastKnownDraft.current = draftIssue.id
    }
  }, [draftIssue.id, formRepository])

  const isRepositoryResolved = useMemo(() => {
    // Only process changes if repository data is streamed,
    // this is to prevent racing condition that leads to infinite loop
    const isRepositoryStreamed = draftIssue.repository != null || !draftIssue.isStreaming
    return isRepositoryStreamed && !loadingDraftIssueRepo
  }, [draftIssue.isStreaming, draftIssue.repository, loadingDraftIssueRepo])

  // Update resolving state
  useEffect(() => {
    if (!isRepositoryResolved) {
      isResolvingFormRepository.current = true
    }
  }, [isRepositoryResolved])

  const onRepositoryChange = useCallback(
    (newRepository: RepositoryPickerRepository | undefined, skipVersionBump: boolean) => {
      if (
        lastKnownFormRepository.current === undefined ||
        !isEqual(lastKnownFormRepository.current?.nameWithOwner, newRepository?.nameWithOwner)
      ) {
        onChange({repository: newRepository?.nameWithOwner}, skipVersionBump)
        lastKnownFormRepository.current = newRepository ?? null
      }
    },
    [onChange],
  )

  // Updates from resolving draftIssue.repository. It only runs after:
  // - Opening a preview from streaming
  // - Opening a preview from streamed block
  // - Switching to a different draft
  useEffect(() => {
    // Ignore when repository are not loaded and is not resolved for initial load
    if (!isRepositoryResolved) {
      return
    }

    if (resolvedDraftIssueRepo != null) {
      setFormRepository(resolvedDraftIssueRepo)
      onRepositoryChange(resolvedDraftIssueRepo, true)
    } else if (topRepos.length > 0) {
      setFormRepository(topRepos[0])
      onRepositoryChange(topRepos[0], true)
    } else {
      setFormRepository(undefined)
      onRepositoryChange(undefined, true)
    }
  }, [isRepositoryResolved, onRepositoryChange, resolvedDraftIssueRepo, setFormRepository, topRepos])

  // Update resolving state
  useEffect(() => {
    if (!isResolvingFormRepository.current || lastKnownFormRepository.current === undefined || !isRepositoryResolved) {
      return
    }

    if (resolvedDraftIssueRepo != null && resolvedDraftIssueRepo === formRepository) {
      isResolvingFormRepository.current = false
    } else if (resolvedDraftIssueRepo == null && topRepos.length > 0 && formRepository === topRepos[0]) {
      isResolvingFormRepository.current = false
    } else if (resolvedDraftIssueRepo == null && topRepos.length <= 0 && formRepository === undefined) {
      isResolvingFormRepository.current = false
    }
  }, [
    draftIssue.id, // Make sure to re-evaluate when switching drafts
    isRepositoryResolved,
    resolvedDraftIssueRepo,
    topRepos,
    formRepository,
  ])

  // Process form changes if not resolving from draftIssue data
  useEffect(() => {
    if (!isResolvingFormRepository.current) {
      onRepositoryChange(formRepository, false)
    }
  }, [formRepository, onRepositoryChange])
}
